import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES, ROLE_LABELS, isAtLeast } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash2, KeyRound, History, Mail, Send, LockOpen } from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString();
}

const ROLE_TONE = {
  [ROLES.SUPER_ADMIN]: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  [ROLES.ADMIN]: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  [ROLES.CONTRIBUTOR]: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  [ROLES.GENERAL]: "text-zinc-400 border-zinc-700 bg-zinc-800/40",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isSuper = isAtLeast(me, ROLES.SUPER_ADMIN);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [loginsFor, setLoginsFor] = useState(null); // {id,email}
  const [loginEvents, setLoginEvents] = useState([]);
  const [loginLoading, setLoginLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => [...users].sort((a, b) => (b.login_count || 0) - (a.login_count || 0)), [users]);

  const openLogins = async (u) => {
    setLoginsFor(u);
    setLoginEvents([]);
    setLoginLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/logins`);
      setLoginEvents(data);
    } catch {
      toast.error("Failed to load login history");
    } finally {
      setLoginLoading(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const resendInvite = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invite re-sent to ${u.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  const unlockUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`${u.email} unlocked`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="admin-users-title">Users</h1>
          <p className="text-sm text-zinc-500">Manage accounts, roles, and view login activity.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="admin-new-user-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 gap-2 h-9">
          <Plus className="w-4 h-4" /> New User
        </Button>
      </div>

      <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-900 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500 text-center">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500 text-center"># Logins</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500 text-center">Last Login</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-mono text-zinc-500 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="border-zinc-900"><TableCell colSpan={6} className="text-center text-zinc-500 py-8 text-sm">Loading…</TableCell></TableRow>
            )}
            {!loading && sorted.map((u) => (
              <TableRow key={u.id} className="border-zinc-900 hover:bg-zinc-900/40" data-testid={`admin-user-row-${u.email}`}>
                <TableCell className="py-2 text-sm font-mono text-zinc-200">
                  {u.email}
                  {u.id === me?.id && <span className="ml-2 text-[10px] text-zinc-500">(you)</span>}
                  {u.pending_invite && <span className="ml-2 text-[9px] px-1 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono uppercase">Pending</span>}
                  {u.locked_until && <span className="ml-2 text-[9px] px-1 py-0.5 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-400 font-mono uppercase" title={`Locked until ${new Date(u.locked_until).toLocaleString()}`}>Locked</span>}
                </TableCell>
                <TableCell className="py-2 text-sm text-zinc-300">{u.name}</TableCell>
                <TableCell className="py-2 text-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border font-mono uppercase tracking-wider ${ROLE_TONE[u.role]}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell className="py-2 text-sm text-center font-mono text-zinc-300">{u.login_count || 0}</TableCell>
                <TableCell className="py-2 text-xs text-center font-mono text-zinc-400" title={u.last_login_at || ""}>{fmtWhen(u.last_login_at)}</TableCell>
                <TableCell className="py-2 text-center">
                  <div className="inline-flex items-center gap-1">
                    {u.locked_until && (
                      <button title="Unlock account (clear failed login attempts)" onClick={() => unlockUser(u)} data-testid={`admin-unlock-${u.email}`} className="p-1 text-zinc-500 hover:text-rose-400">
                        <LockOpen className="w-4 h-4" />
                      </button>
                    )}
                    {u.pending_invite && (
                      <button title="Resend invite email" onClick={() => resendInvite(u)} data-testid={`admin-resend-${u.email}`} className="p-1 text-zinc-500 hover:text-amber-400">
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    <button title="View login history" onClick={() => openLogins(u)} data-testid={`admin-logins-${u.email}`} className="p-1 text-zinc-500 hover:text-blue-400">
                      <History className="w-4 h-4" />
                    </button>
                    <button title="Edit / change role" onClick={() => setEditTarget(u)} data-testid={`admin-edit-${u.email}`} className="p-1 text-zinc-500 hover:text-blue-400">
                      <ShieldCheck className="w-4 h-4" />
                    </button>
                    <button title="Delete" onClick={() => deleteUser(u)} disabled={u.id === me?.id} data-testid={`admin-delete-${u.email}`} className="p-1 text-zinc-500 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} isSuper={isSuper} onCreated={load} />
      <EditUserDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={load} isSuper={isSuper} me={me} />
      <LoginsDialog user={loginsFor} onClose={() => setLoginsFor(null)} events={loginEvents} loading={loginLoading} />
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, isSuper, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: ROLES.GENERAL });
  const [mode, setMode] = useState("invite"); // "invite" | "password"
  const [busy, setBusy] = useState(false);
  const allowedRoles = isSuper
    ? [ROLES.GENERAL, ROLES.CONTRIBUTOR, ROLES.ADMIN, ROLES.SUPER_ADMIN]
    : [ROLES.GENERAL, ROLES.CONTRIBUTOR];

  useEffect(() => {
    if (open) { setForm({ email: "", password: "", name: "", role: ROLES.GENERAL }); setMode("invite"); }
  }, [open]);

  const submit = async () => {
    if (!form.email) { toast.error("Email is required"); return; }
    if (mode === "password" && !form.password) { toast.error("Password is required"); return; }
    setBusy(true);
    try {
      const payload = {
        email: form.email,
        name: form.name,
        role: form.role,
        ...(mode === "password" ? { password: form.password, send_invite: false } : { send_invite: true }),
      };
      const { data } = await api.post("/admin/users", payload);
      if (data.invited) toast.success(`Invite sent to ${data.email}`);
      else toast.success("User created");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-md rounded-sm" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Create User</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mb-1">
          <button
            type="button"
            onClick={() => setMode("invite")}
            data-testid="create-mode-invite"
            className={`text-xs font-mono uppercase tracking-wider py-2 rounded-sm border ${mode === "invite" ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-zinc-800 bg-transparent text-zinc-500 hover:text-zinc-200"}`}
          >
            <Mail className="w-3.5 h-3.5 inline mr-1" /> Send invite email
          </button>
          <button
            type="button"
            onClick={() => setMode("password")}
            data-testid="create-mode-password"
            className={`text-xs font-mono uppercase tracking-wider py-2 rounded-sm border ${mode === "password" ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-zinc-800 bg-transparent text-zinc-500 hover:text-zinc-200"}`}
          >
            <KeyRound className="w-3.5 h-3.5 inline mr-1" /> Set password
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-user-email" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 font-mono mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Name (optional)</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 mt-1" />
          </div>
          {mode === "password" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-user-password" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 font-mono mt-1" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="create-user-role" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {allowedRoles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            {!isSuper && <p className="text-[10px] text-zinc-600 mt-1">Only Super Admin can create Admin / Super Admin accounts.</p>}
          </div>
          {mode === "invite" && (
            <p className="text-[10px] text-zinc-500 border-l-2 border-blue-500/40 pl-2">
              We'll email a one-time "Set your password" link (expires in 7 days). You never see or handle their password.
            </p>
          )}
          <Button onClick={submit} disabled={busy} data-testid="create-user-submit" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-9 mt-2">
            {mode === "invite" ? "Send Invite" : "Create User"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, onClose, onSaved, isSuper, me }) {
  const [form, setForm] = useState({ name: "", role: ROLES.GENERAL, password: "" });
  useEffect(() => {
    if (target) setForm({ name: target.name || "", role: target.role, password: "" });
  }, [target]);

  if (!target) return null;
  const canChangeRole = isSuper || (target.role !== ROLES.SUPER_ADMIN && target.role !== ROLES.ADMIN);
  const allowedRoles = isSuper
    ? [ROLES.GENERAL, ROLES.CONTRIBUTOR, ROLES.ADMIN, ROLES.SUPER_ADMIN]
    : [ROLES.GENERAL, ROLES.CONTRIBUTOR];

  const submit = async () => {
    const patch = { name: form.name };
    if (canChangeRole && form.role !== target.role) patch.role = form.role;
    if (form.password) patch.password = form.password;
    try {
      await api.patch(`/admin/users/${target.id}`, patch);
      toast.success("Saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-md rounded-sm" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Edit User</DialogTitle>
        </DialogHeader>
        <div className="text-xs font-mono text-zinc-500 mb-2">{target.email}</div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={!canChangeRole}>
              <SelectTrigger data-testid="edit-user-role" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {allowedRoles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            {!canChangeRole && <p className="text-[10px] text-zinc-600 mt-1">Only Super Admin can change admin/super admin roles.</p>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset password (optional)</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="edit-user-password" placeholder="Leave blank to keep current" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 font-mono mt-1" />
          </div>
          <Button onClick={submit} data-testid="edit-user-submit" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-9 mt-2">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoginsDialog({ user, onClose, events, loading }) {
  if (!user) return null;
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-2xl max-h-[80vh] overflow-y-auto rounded-sm" data-testid="logins-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg inline-flex items-center gap-2">
            <History className="w-4 h-4 text-blue-400" /> Login History · <span className="text-blue-400">{user.email}</span>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No logins recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                <th className="text-left font-mono py-2 font-normal">When</th>
                <th className="text-left font-mono py-2 font-normal">IP</th>
                <th className="text-left font-mono py-2 font-normal">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-b border-zinc-900/60">
                  <td className="py-1.5 font-mono text-zinc-300">{fmtWhen(e.at)}</td>
                  <td className="py-1.5 font-mono text-zinc-400">{e.ip || "—"}</td>
                  <td className="py-1.5 text-zinc-500 text-xs truncate max-w-[420px]" title={e.user_agent || ""}>{e.user_agent || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
